import React, { useState } from 'react';
import {
    IconTextButton,
    ITreeSelectDataSource,
    notification,
    AdvanceTable,
    IAdvanceTableToolbarProps,
    Typography,
    showInformationDialog,
    showConfirmDialog,
    LabelFieldItem,
    CheckboxField,
    Spinner,
} from 'ui-lib';
import fileTransferCss from './new-file-transfer.css';
import { TableUtils } from 'pages/private/components/table-utils';
import { useStores } from 'store';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { FileTranferExecutionDto } from 'dto/file-transfer.dto';
import { DeviceGroupSelect } from 'pages/private/components/device-groups-select';
import { useUrlSearch } from 'hooks/use-search.hook';

interface ITrackingTransfer {
    selectedClientAccountId: number;
    deviceGroupsDataSource: ITreeSelectDataSource[];
    deviceGroupLoading: boolean;
}

const timeColumns = [
    TableUtils.createBasicFilterTimeColumn({ key: 'createdAt', title: 'Created At' }),
    TableUtils.createBasicFilterTimeColumn({ key: 'updatedAt', title: 'Updated At' }),
];
function isExecutionCancelable(execution: any) {
    return execution.status === 'IN_PROGRESS';
}

export const TrackingTab = (props: ITrackingTransfer) => {
    const { deviceGroupsStore } = useStores();
    
    const { getSearchParam, updateSearchParams } = useUrlSearch();
    const [selectedGroup, setSelectedGroup] =  React.useState<string>('');
    const [fileTransferExecutions, setFileTransferExecutions] = React.useState<FileTranferExecutionDto[]>([]);
    const { withSaving, withLoading, loading, saving, errorMessages } = useLoadingStates();

    async function loadFileTransferExecutions() {
        return withLoading(async () => {
            const executions = await deviceGroupsStore.getFileTransferExecutions(props.selectedClientAccountId, selectedGroup);
            setFileTransferExecutions(executions);
        });
    }
    
    React.useEffect(() => {
        setSelectedGroup( () => {
            const paramDeviceGroup = getSearchParam('deviceGroup') || '';
            return props.deviceGroupsDataSource?.find(dg => dg.treeDataNode.value === paramDeviceGroup) ? paramDeviceGroup : '';
        });
    }, [props.deviceGroupsDataSource]);

    React.useEffect(() => {
        if (!selectedGroup || !props.selectedClientAccountId) {
            return;
        }
        loadFileTransferExecutions();
    }, [selectedGroup, props.selectedClientAccountId]);

    const trackingTableToolbar: IAdvanceTableToolbarProps = {
        onReload: loadFileTransferExecutions,
    };

    React.useEffect(() => {
        if (isNullOrEmpty(errorMessages)) {
            return;
        }
        notification.error({
            message: 'Error',
            description: getErrorMessage(errorMessages[0]),
            placement: 'topRight',
        });
    }, [errorMessages]);

    const selectDeviceGroupComponent = (
        <DeviceGroupSelect
            placeholder='Select Device Group'
            treeDataSource={props.deviceGroupsDataSource}
            expandAll={true}
            onChange={(value): void => {
                setSelectedGroup(value || '');
                updateSearchParams({ deviceGroup: value || null });
            }}
            disabled={props.deviceGroupLoading}
            className={fileTransferCss.orgSelect}
            value={selectedGroup}
        />
    );

    const fileTransferColumns = [
        TableUtils.createBasicFilterColumn({
            key: 'file_name',
            title: 'File Name',
        }),
        TableUtils.createBasicFilterColumn({
            key: 'version',
            title: 'Version',
        }),
        TableUtils.createBasicFilterColumn({
            key: 'directory',
            title: 'Destination Path',
        }),
        TableUtils.createBasicFilterColumn({
            key: 'permissions',
            title: 'Permissions',
        }),
    ];

    return (
        <React.Fragment>
            <Spinner spinning={props.deviceGroupLoading} className={fileTransferCss.spinner}>
                <AdvanceTable
                    toolbar={trackingTableToolbar}
                    title={selectDeviceGroupComponent}
                    loadingState={loading || saving}
                    table={{
                        rowKey: 'jobId',
                        columns: [
                            TableUtils.createBasicFilterColumn({
                                key: 'jobId',
                                title: 'File Transfer Execution',
                                columnExtraOptions: {
                                    render: (text: string, record: FileTranferExecutionDto) => (
                                        <Typography.Link
                                            onClick={() =>
                                                showInformationDialog({
                                                    title: record.jobId,
                                                    modalType: 'info',
                                                    width: '1000px',
                                                    content: (
                                                        <div>
                                                            <LabelFieldItem
                                                                key='name'
                                                                code='name'
                                                                label='Description'
                                                                colon={true}
                                                                labelAlign='right'
                                                                initialValue={record.description}
                                                            />
                                                            <LabelFieldItem
                                                                labelAlign='right'
                                                                label='Request Reboot'
                                                                code='requestReboot'
                                                                colon={true}
                                                                initialValue={
                                                                    <CheckboxField
                                                                        label=''
                                                                        checked={record.requestReboot}
                                                                    />
                                                                }
                                                            />
                                                            <LabelFieldItem
                                                                labelAlign='right'
                                                                label='Out of Service Windows'
                                                                colon={true}
                                                                code='outOfServiceWindows'
                                                                initialValue={
                                                                    <ul style={{ listStyle: 'none', marginTop: '5px' }}>
                                                                        {record.outOfServiceWindows.map((window, index) => (
                                                                            <li key={index} style={{ margin: '0px' }}>
                                                                                {window.startTime} - {window.endTime}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                }
                                                            />
                                                            <AdvanceTable
                                                                title='Transfer Files'
                                                                table={{
                                                                    columns: fileTransferColumns,
                                                                    dataSource: record.files || [],
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                })
                                            }
                                        >
                                            {text}
                                        </Typography.Link>
                                    ),
                                },
                            }),
                            TableUtils.createBasicFilterColumn({ key: 'status', title: 'Status' }),
                            TableUtils.createBasicFilterColumn({ key: 'description', title: 'Description' }),
                            ...timeColumns,
                            TableUtils.createExtraColumn({
                                key: 'actions',
                                title: 'Actions',
                                render: (text: string, record: any) => (
                                    <IconTextButton
                                        type='link'
                                        label='Cancel'
                                        disabled={!isExecutionCancelable(record)}
                                        onClick={() => {
                                            showConfirmDialog({
                                                title: 'Cancel File Transfer Execution',
                                                content: `Are you sure you want to cancel file transfer execution ${record.jobId}?`,
                                                onOk: async () => {
                                                    withSaving(async () => {
                                                        await deviceGroupsStore.cancelFileTransferExecution(
                                                            props.selectedClientAccountId,
                                                            selectedGroup,
                                                            record.jobId,
                                                        );
                                                        await loadFileTransferExecutions();
                                                    });
                                                },
                                            });
                                        }}
                                    />
                                ),
                            }),
                        ],
                        dataSource: fileTransferExecutions,
                    }}
                />
            </Spinner>
        </React.Fragment>
    );
};
